import { useState } from "react";
import { Flag, ShieldAlert, TriangleAlert } from "lucide-react";
import { useAdminApi } from "../lib/api";
import { useI18n, serverError } from "../i18n";

/**
 * 檢舉審核
 * ═══════════════════════════════════════════════════════════════
 *
 * ── 為什麼每一列都顯示「這個帳號被檢舉過幾次」 ──────────────
 *
 * 一次檢舉可能是誤會、可能是報復、可能是競爭對手。
 * 五次來自不同人的檢舉不是。
 *
 * 那個數字是審核者最需要、卻最不該自己去數的東西 ——
 * 要他每看一筆就去搜尋一次帳號，實務上等於沒有這個資訊。
 * 所以由伺服器一次算好（一個 GROUP BY，不是 30 次查詢）。
 *
 * ── 為什麼「駁回」也要寫理由 ────────────────────────────────
 *
 * 半年後有人問「為什麼當初沒有處理」，一筆沒有理由的紀錄
 * 和沒有紀錄一樣。而檢舉紀錄正是那種會在最糟的時機
 * 被翻出來的東西 —— 通常是在事情已經鬧大之後。
 *
 * 只有「處理中」不用理由：那是「我看到了，還在查」，
 * 本來就還沒有結論。
 */

interface Report {
  id: string;
  market: string;
  targetKind: string;
  targetId: string;
  targetUserId: string | null;
  reason: string;
  detail: string | null;
  status: string;
  createdAt: string;
  resolution: string | null;
  reporterEmail: string | null;
  reporterName: string | null;
  targetEmail: string | null;
  targetName: string | null;
  targetSuspendedAt: string | null;
  targetReportCount: number;
}

interface Page {
  reports: Report[];
  total: number;
  page: number;
  pageSize: number;
}

type Status = "open" | "reviewing" | "actioned" | "dismissed" | "all";

const TABS: Status[] = ["open", "reviewing", "actioned", "dismissed", "all"];

export default function Reports() {
  const { t } = useI18n();
  const [status, setStatus] = useState<Status>("open");
  const [done, setDone] = useState<Record<string, string>>({});

  const { data, error, loading } = useAdminApi<Page>(`/reports?status=${status}`, [status]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="flex items-center gap-2 text-lg font-semibold text-ink">
          <Flag className="size-5 text-brand-300" />
          {t("rep.title")}
        </h1>
        <p className="mt-1.5 text-xs leading-relaxed text-ink-muted">{t("rep.lede")}</p>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {TABS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatus(s)}
            className={
              "rounded-pill px-3.5 py-1.5 text-xs font-medium transition " +
              (status === s ? "nm-inset text-ink" : "text-ink-muted hover:text-ink")
            }
          >
            {t(`rep.tab.${s}` as never)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-32 rounded-card shimmer" />
          ))}
        </div>
      ) : error ? (
        <p className="rounded-card nm-inset p-5 text-sm text-brand-300">{error}</p>
      ) : !data || data.reports.length === 0 ? (
        <p className="rounded-card nm-inset p-8 text-center text-sm text-ink-muted">
          {t("rep.empty")}
        </p>
      ) : (
        <div className="space-y-3">
          {data.reports.map((r) => (
            <Row
              key={r.id}
              report={r}
              resolved={done[r.id]}
              onResolved={(next) => setDone((d) => ({ ...d, [r.id]: next }))}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Row({
  report,
  resolved,
  onResolved,
}: {
  report: Report;
  resolved?: string;
  onResolved: (status: string) => void;
}) {
  const { t } = useI18n();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function resolve(status: "reviewing" | "actioned" | "dismissed") {
    if (status !== "reviewing" && !note.trim()) {
      setError(t("rep.needNote"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/reports/${report.id}/resolve`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ status, resolution: note.trim() || undefined }),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        setError(serverError(payload, t, t("rep.failed")));
        return;
      }
      onResolved(status);
    } catch {
      setError(t("rep.network"));
    } finally {
      setBusy(false);
    }
  }

  const status = resolved ?? report.status;
  const settled = status === "actioned" || status === "dismissed";

  return (
    <article className="rounded-card nm p-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-pill nm-inset px-2.5 py-1 text-[11px] font-medium text-ink">
              {t(`rep.reason.${report.reason}` as never)}
            </span>
            <span className="rounded-pill nm-inset px-2.5 py-1 text-[11px] text-ink-muted">
              {t(`rep.kind.${report.targetKind}` as never)}
            </span>
            <span className="rounded-pill nm-inset px-2.5 py-1 text-[11px] text-ink-muted">
              {report.market}
            </span>
            {/*
              被檢舉三次以上就標紅。

              三是一個判斷：一次是雜訊，兩次可能還是巧合，
              三次來自不同人就值得先看這一筆。
            */}
            {report.targetReportCount >= 3 && (
              <span className="flex items-center gap-1 rounded-pill bg-brand-400/15 px-2.5 py-1 text-[11px] font-semibold text-brand-300">
                <TriangleAlert className="size-3" />
                {t("rep.repeat", { n: report.targetReportCount })}
              </span>
            )}
            {report.targetSuspendedAt && (
              <span className="flex items-center gap-1 rounded-pill nm-inset px-2.5 py-1 text-[11px] text-ink-muted">
                <ShieldAlert className="size-3" />
                {t("rep.alreadySuspended")}
              </span>
            )}
          </div>

          <dl className="mt-3 grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
            <div className="flex gap-2">
              <dt className="text-ink-muted">{t("rep.reporter")}</dt>
              <dd className="truncate text-ink-soft">
                {report.reporterName ?? report.reporterEmail ?? "—"}
              </dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-ink-muted">{t("rep.target")}</dt>
              <dd className="truncate text-ink-soft">
                {report.targetName ?? report.targetEmail ?? report.targetId}
              </dd>
            </div>
          </dl>
        </div>

        <time className="shrink-0 text-[11px] text-ink-muted">
          {new Date(report.createdAt).toLocaleString()}
        </time>
      </header>

      {report.detail && (
        <p className="mt-3 whitespace-pre-wrap rounded-xl nm-inset px-4 py-3 text-xs leading-relaxed text-ink-soft">
          {report.detail}
        </p>
      )}

      {settled ? (
        <p className="mt-3 rounded-xl nm-inset px-4 py-3 text-xs text-ink-muted">
          {t(`rep.tab.${status}` as never)}
          {report.resolution ? ` — ${report.resolution}` : ""}
        </p>
      ) : (
        <div className="mt-4 space-y-2.5">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={1000}
            placeholder={t("rep.notePlaceholder")}
            className="w-full resize-y rounded-xl nm-inset bg-transparent px-4 py-2.5 text-xs text-ink outline-none focus:ring-1 focus:ring-brand-400/40"
          />

          {error && (
            <p role="alert" className="text-[11px] text-brand-300">
              {error}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => resolve("actioned")}
              disabled={busy}
              className="rounded-pill nm-pressable px-4 py-2 text-xs font-semibold text-ink disabled:opacity-40"
            >
              {t("rep.action.actioned")}
            </button>
            <button
              type="button"
              onClick={() => resolve("dismissed")}
              disabled={busy}
              className="rounded-pill nm-pressable px-4 py-2 text-xs font-medium text-ink-soft disabled:opacity-40"
            >
              {t("rep.action.dismissed")}
            </button>
            {status === "open" && (
              <button
                type="button"
                onClick={() => resolve("reviewing")}
                disabled={busy}
                className="rounded-pill px-4 py-2 text-xs text-ink-muted hover:text-ink disabled:opacity-40"
              >
                {t("rep.action.reviewing")}
              </button>
            )}
          </div>
        </div>
      )}
    </article>
  );
}
